"use no memo";
"use client";

import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useRef } from "react";
import { useCallbackRef, useDedupedItems } from "../../hooks";
import { TABLE_SIZES } from "../Table/constants";
import { TableProvider } from "../Table/TableProvider";
import { VirtualizedTableView } from "./VirtualizedTableView";
const isTableMode = props => {
  return props.scrollMode === "table";
};
const VirtualizedTable = ({
  items,
  ...props
}) => {
  const itemKeyRef = useRef(props.itemKey);
  const dedupedItems = useDedupedItems(items, props.itemKey);
  if (isTableMode(props)) {
    return <TableScrollVirtualizedTable {...props} itemKey={itemKeyRef.current} items={dedupedItems} />;
  }
  return <WindowScrollVirtualizedTable {...props} itemKey={itemKeyRef.current} items={dedupedItems} />;
};
const getDefaultOverscan = rowHeight => {
  // 6 rows at 32px height
  return Math.ceil(190 / rowHeight);
};
const WindowScrollVirtualizedTable = ({
  items,
  overscanBy,
  tableBodyRef,
  setTableBodyRef: setTableBodyRefProp,
  initialRect,
  itemKey,
  size = "md",
  overrides,
  dividers,
  ...props
}) => {
  const [tableRef, setTableRefCallback] = useCallbackRef(tableBodyRef);
  const getItemKey = useCallback(index => itemKey?.(items[index], index) ?? index, [itemKey, items]);
  const tableSize = TABLE_SIZES[size];
  const gap = overrides?.size?.gap ?? tableSize.gap;
  const gapAccountingForDividers = dividers ? 0 : gap;
  const rowHeight = overrides?.size?.height ?? tableSize.rowHeight;
  const overscan = overscanBy ?? getDefaultOverscan(rowHeight);
  const rowVirtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => rowHeight,
    getItemKey,
    overscan,
    scrollMargin: tableRef.current?.offsetTop,
    initialRect,
    gap: gapAccountingForDividers
  });
  const setTableRef = ref => {
    if (!ref || ref === tableRef.current) {
      return;
    }
    setTableRefCallback(ref);
    setTableBodyRefProp?.(ref);
  };
  return <TableProvider dividers={dividers} size={size}>
      <VirtualizedTableView gap={gapAccountingForDividers} itemKey={itemKey} items={items} rowVirtualizer={rowVirtualizer} setTableRef={setTableRef} tableRef={tableRef} {...props} />
    </TableProvider>;
};
const TableScrollVirtualizedTable = ({
  items,
  overscanBy,
  scrollContainerRef,
  tableBodyRef,
  setTableBodyRef: setTableBodyRefProp,
  initialRect,
  className,
  size = "md",
  overrides,
  itemKey,
  dividers,
  ...props
}) => {
  const [tableRef, setTableRefCallback] = useCallbackRef(tableBodyRef);
  const tableSize = TABLE_SIZES[size];
  const gap = overrides?.size?.gap ?? tableSize.gap;
  const gapAccountingForDividers = dividers ? 0 : gap;
  const rowHeight = overrides?.size?.height ?? tableSize.rowHeight;
  const overscan = overscanBy ?? getDefaultOverscan(rowHeight);
  const getItemKey = useCallback(index => itemKey?.(items[index], index) ?? index, [itemKey, items]);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => rowHeight,
    getItemKey,
    getScrollElement: () => scrollContainerRef?.current ?? tableRef.current,
    overscan,
    initialRect,
    scrollMargin: tableRef.current?.offsetTop,
    gap: gapAccountingForDividers
  });
  const setTableRef = ref => {
    if (!ref || ref === tableRef.current) {
      return;
    }
    setTableRefCallback(ref);
    setTableBodyRefProp?.(ref);
  };
  return <TableProvider dividers={dividers} size={size}>
      <VirtualizedTableView className={className} gap={gapAccountingForDividers} itemKey={itemKey} items={items} rowVirtualizer={rowVirtualizer} setTableRef={setTableRef} tableRef={tableRef} {...props} />
    </TableProvider>;
};

/**
 * A table that automatically virtualizes its rows to improve performance.
 * Rows that are out of the viewport are not rendered.
 */
export { VirtualizedTable };